import React, { useState, useEffect } from 'react';
import { charactersAPI } from '../../services/api';
import CharacterEditorModal from './CharacterEditorModal';
import CharacterCreationModal from './CharacterCreationModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import CharacterSheetModal from './CharacterSheetModal';
import './Characters.css';

function Characters() {
  const [characters, setCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCharacterId, setEditingCharacterId] = useState(null);
  const [deletingCharacter, setDeletingCharacter] = useState(null); // { id, name }
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingCharacter, setIsCreatingCharacter] = useState(false);

  const [sheetCharacter, setSheetCharacter] = useState(null);
  const [showSheetModal, setShowSheetModal] = useState(false);
  const [isLoadingSheet, setIsLoadingSheet] = useState(false);

  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await charactersAPI.getMyCharacters();
      setCharacters(res.data?.characters || []);
    } catch (err) {
      console.error('Failed to load characters:', err);
      setError(err?.response?.data?.error || 'Failed to load characters');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCharacter) return;

    try {
      setIsDeleting(true);
      await charactersAPI.deleteCharacter(deletingCharacter.id);

      await loadCharacters();
      setDeletingCharacter(null);
    } catch (err) {
      console.error('Failed to delete character:', err);
      alert(err?.response?.data?.error || 'Failed to delete character');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCharacterCreated = () => {
    loadCharacters();
  };

  const closeSheetModal = () => {
    setShowSheetModal(false);
    setSheetCharacter(null);
  };

  const handleViewSheet = async (characterId) => {
    try {
      setIsLoadingSheet(true);
      const res = await charactersAPI.getCharacter(characterId);

      // backend may return { character } or the character directly depending on implementation
      const c = res.data?.character || res.data;

      setSheetCharacter(c);
      setShowSheetModal(true);
    } catch (err) {
      console.error('Failed to load character sheet:', err);
      alert(err?.response?.data?.error || 'Failed to load character sheet');
    } finally {
      setIsLoadingSheet(false);
    }
  };

  // Calculate primary stat (highest stat)
  const getPrimaryStat = (character) => {
    const stats = {
      Physique: character.physique || 0,
      Coordination: character.coordination || 0,
      Logic: character.logic || 0,
      Perception: character.perception || 0,
      Presence: character.presence || 0,
      Resilience: character.resilience || 0
    };

    let maxStat = 'Physique';
    let maxValue = stats.Physique;

    Object.entries(stats).forEach(([stat, value]) => {
      if (value > maxValue) {
        maxValue = value;
        maxStat = stat;
      }
    });

    return maxStat;
  };

  return (
    <div className="characters-section">
      <div className="section-header">
        <h2>My Characters</h2>
        <button
          className="btn-primary"
          onClick={() => setIsCreatingCharacter(true)}
        >
          Create New Character
        </button>
      </div>

      {isLoading ? (
        <div className="empty-state">
          <p>Loading your characters...</p>
        </div>
      ) : error ? (
        <div className="empty-state">
          <p>{error}</p>
        </div>
      ) : characters.length === 0 ? (
        <div className="empty-state">
          <p>You haven't created any characters yet.</p>
          <p className="hint">Click "Create New Character" above to get started!</p>
        </div>
      ) : (
        <div className="characters-grid">
          {characters.map(character => (
            <div key={character.id} className="character-card">
              <div className="character-header">
                <div>
                  <h3>{character.name}</h3>
                </div>
                <span className="status-badge active">Active</span>
              </div>

              <div className="character-stats">
                <div className="stat-item">
                  <span className="stat-label">Type:</span>
                  <span className="stat-value">{character.characterType}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">XP Available:</span>
                  <span className="stat-value">{character.availableXp}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">Primary Stat:</span>
                  <span className="stat-value">{getPrimaryStat(character)}</span>
                </div>
              </div>

              <div className="character-campaign">
                <span className="campaign-label">Campaign:</span>
                <span className="campaign-name">{character.campaignName || 'No Campaign'}</span>
              </div>

              {character.gmName && (
                <div className="character-campaign">
                  <span className="campaign-label">GM:</span>
                  <span className="campaign-name">{character.gmName}</span>
                </div>
              )}

              <div className="character-actions button-row">
                <button
                  className="btn-secondary"
                  onClick={() => handleViewSheet(character.id)}
                  disabled={isLoadingSheet}
                  title="View your character sheet"
                >
                  {isLoadingSheet ? 'Loading...' : 'View Sheet'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setEditingCharacterId(character.id)}
                >
                  Edit
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setDeletingCharacter({ id: character.id, name: character.name })}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CharacterCreationModal
        isOpen={isCreatingCharacter}
        onClose={() => setIsCreatingCharacter(false)}
        onCharacterCreated={handleCharacterCreated}
      />

      <CharacterEditorModal
        isOpen={!!editingCharacterId}
        characterId={editingCharacterId}
        onClose={() => setEditingCharacterId(null)}
        onUpdated={loadCharacters}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingCharacter}
        onClose={() => setDeletingCharacter(null)}
        onConfirm={handleDelete}
        characterName={deletingCharacter?.name}
        isDeleting={isDeleting}
      />

      <CharacterSheetModal
        isOpen={showSheetModal}
        character={sheetCharacter}
        onClose={closeSheetModal}
      />
    </div>
  );
}

export default Characters;
